import { useForm } from 'react-hook-form';
import { useId } from 'react';
import { User } from '@ui/types/user';
import { AccountSettingsPanel } from '../account-settings-panel';
import { Button } from '@ui/buttons/button';
import { Form } from '@ui/forms/form';
import { FormTextField } from '@ui/forms/input-field/text-field/text-field';
import { useUpdateAccountDetails } from './update-account-details';
import { Trans } from '@ui/i18n/trans';
import { useUploadAvatar } from '../avatar/upload-avatar';
import { useRemoveAvatar } from '../avatar/remove-avatar';
import { FormImageSelector } from '@common/uploads/components/image-selector';
import { FileUploadProvider } from '@common/uploads/uploader/file-upload-provider';
import { AccountSettingsId } from '@common/auth/ui/account-settings/account-settings-sidenav';

interface Props {
  user: User;
}
export function BasicInfoPanel({ user }: Props) {
  const uploadAvatar = useUploadAvatar({ user });
  const removeAvatar = useRemoveAvatar({ user });
  const formId = useId();
  const form = useForm<Partial<Omit<User, 'subscriptions'>>>({
    defaultValues: {
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      image: user.image,
    },
  });
  const updateDetails = useUpdateAccountDetails(user.id, form);

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.AccountDetails}
      title={<Trans message="Update name and profile image" />}
      actions={
        <Button
          type="submit"
          variant="flat"
          color="primary"
          size="md"
          className="px-32 py-12 font-bold tracking-wide transition-all hover:scale-105 active:scale-95"
          form={formId}
          disabled={updateDetails.isPending || !form.formState.isValid}
        >
          <Trans message="Save Changes" />
        </Button>
      }
    >
      <Form
        form={form}
        className="flex flex-col-reverse items-center gap-40 md:flex-row md:items-start md:gap-80"
        onSubmit={newDetails => {
          updateDetails.mutate(newDetails);
        }}
        id={formId}
      >
        <div className="w-full flex-auto">
          <FormTextField
            className="mb-24"
            name="first_name"
            label={<Trans message="First name" />}
          />
          <FormTextField
            name="last_name"
            label={<Trans message="Last name" />}
          />
        </div>
        <FileUploadProvider>
          <FormImageSelector
            className="md:ml-auto"
            variant="avatar"
            previewSize="w-120 h-120"
            showRemoveButton
            name="image"
            diskPrefix="avatars"
            label={<Trans message="Profile image" />}
            onChange={url => {
              if (url) {
                uploadAvatar.mutate({ url });
              } else {
                removeAvatar.mutate();
              }
            }}
          />
        </FileUploadProvider>
      </Form>
    </AccountSettingsPanel>
  );
}
